import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  ProductAnalyticsService,
  ProductStateService,
} from '@usf/ngrx-product';
import { SearchRequest } from '@usf/product-types';
import { combineLatest, Observable, of } from 'rxjs';
import { ProductService } from 'src/app/shared/services/product/product.service';
import { productByIdSelectorWithSelectedCustomer } from '../../shared/selectors/product.selectors';
import { productDetailsViewModelSelector } from '@product-detail/selectors/product-detail.selector';
import {
  SearchResultViewModel,
  SearchViewModel,
} from '../shared/models/search-view.model';
import {
  catchError,
  debounceTime,
  filter,
  first,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  searchResultOrderItem,
  searchResultsSelector,
  searchViewModelSelector,
  topSellersSelector,
} from '../selectors/search-page-selector';
import { OrderItem } from '@usf/ngrx-order';
import { SEARCH_RECORD_PER_BATCH } from '../shared/constants/search-config';
import { ProductPricingService } from '@usf/ngrx-pricing';
import { NavigationHelperService } from 'src/app/shared/helpers/navigation.helpers.service';
import { UsfProductCardModeEnum } from '../../shared/constants/usf-product-card-mode.enum';
import { getExcludeProductNumbersQueryParams } from '../utils/search-filter.util';
import { SearchTracingService } from './search-tracing.service';
import { environment } from 'src/environments/environment';
import { SEARCH_PAGES_SEARCH_KEYS } from '@shared/constants/search-pages.enum';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { throwError } from 'rxjs';
import { ToastService } from '@shared/services/toast/toast.service';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { UserService } from '@app/user/services';
import { coveoTopSellersViewModelSelector } from '@shared/selectors/coveo-merchandising-selectors';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  constructor(
    protected store: Store,
    private productStateService: ProductStateService,
    private productService: ProductService,
    private productPricingService: ProductPricingService,
    private analyticsService: ProductAnalyticsService,
    private navigationHelperService: NavigationHelperService,
    private readonly searchTracingService: SearchTracingService,
    private httpClient: HttpClient,
    private toastService: ToastService,
    private userService: UserService,
  ) {}

  LISTS_FACET_NAME = 'LISTS';
  DEALS_FACET_NAME = 'DEALS';

  getSearchResult(
    request: SearchRequest,
    previousPagesProductNumbers: number[],
  ): Observable<SearchViewModel> {
    let itemAlreadySearched = null;
    this.productStateService
      .searchedTerms(request.searchText)
      .pipe(take(1))
      .subscribe(term => (itemAlreadySearched = term));

    /**
     * Check If result search has been already searched previously
     */
    // TODO: this change will break few functions for browse and list all. Also the filter.
    // Need to find alternative solution
    // if (itemAlreadySearched) {
    //   return this.buildSearchViewModel(request);
    // }

    return this.productStateService.loadSearchResponse(request).pipe(
      first(isReady => isReady),
      switchMap(() => {
        return this.buildSearchViewModel(request, previousPagesProductNumbers);
      }),
    );
  }

  getSearchRequest(
    queryParams: Params,
    mode: UsfProductCardModeEnum,
  ): SearchRequest {
    const requestParams: SearchRequest = {
      ...queryParams,
      recordsPerPage: SEARCH_RECORD_PER_BATCH,
    };

    requestParams.excludeProductNumbers =
      getExcludeProductNumbersQueryParams(queryParams);
    if (
      typeof queryParams?.isGroupSticky === 'string' ||
      queryParams?.isGroupSticky instanceof String
    ) {
      requestParams.isGroupSticky = queryParams.isGroupSticky === 'true';
    }
    if (
      typeof queryParams?.isSticky === 'string' ||
      queryParams?.isSticky instanceof String
    ) {
      requestParams.isSticky = queryParams.isSticky === 'true';
    }
    if (
      typeof queryParams?.didYouMean === 'string' &&
      !!queryParams?.didYouMean
    ) {
      requestParams.didYouMean = +queryParams?.didYouMean;
    }

    if (
      typeof queryParams?.specialOrderExcluded === 'string' ||
      queryParams?.specialOrderExcluded instanceof String
    ) {
      requestParams.specialOrderExcluded =
        queryParams.specialOrderExcluded === 'true';
    }

    if (
      mode === UsfProductCardModeEnum.cartReplacementSearch ||
      mode === UsfProductCardModeEnum.replacementSearch
    ) {
      requestParams.isSubsFlow = true;
      requestParams.excludedProduct =
        getExcludeProductNumbersQueryParams(queryParams);
    }
    if (mode === UsfProductCardModeEnum.addToListSearch) {
      requestParams.pageType = 'all';
    }
    if (mode === UsfProductCardModeEnum.cartReplacementSearch) {
      requestParams.specialOrderExcluded = true;
    }
    return requestParams;
  }

  isSearchConditionValid(request: SearchRequest): boolean {
    return (
      !!request.searchText ||
      !!request.rangeRefinements ||
      !!request.searchFilterProperties ||
      request.excludeProductNumbers?.length > 0
    );
  }

  /**
   * Retrieves the base product information to populate the 'original/base' product
   * that needs to be replaced
   * @param replacementBaseProductNumber
   */
  getReplacementBaseProduct(
    replacementBaseProductNumber: number,
  ): Observable<SearchResultViewModel> {
    this.productService.loadProduct(replacementBaseProductNumber);
    return this.store
      .select(
        productByIdSelectorWithSelectedCustomer(replacementBaseProductNumber),
      )
      .pipe(filter(searchVM => !!searchVM.summary && !!searchVM.inventory));
  }

  buildSearchViewModel(
    searchRequest: SearchRequest,
    previousPagesProductNumbers: number[],
  ): Observable<SearchViewModel> {
    return combineLatest([
      this.store
        .select(searchViewModelSelector)
        .pipe(filter(searchViewModel => !!searchViewModel)),
      this.userService.isGuestUser$(),
    ]).pipe(
      switchMap(([searchViewModel, isGuest]) => {
        searchViewModel.searchRequest = searchRequest;
        if (
          searchViewModel.searchResponse?.totalNumberOfRecords > 0 &&
          !!searchViewModel.searchResponse.productNumbers
        ) {
          //TODO - find a better place to trigger a price load
          this.productService
            .getProductsWithAlternatives([
              ...searchViewModel.searchResponse.productNumbers,
              ...previousPagesProductNumbers,
            ])
            .pipe(
              filter(products => {
                return [...products.values()].every(
                  product => product !== undefined,
                );
              }),
              take(1),
            )
            .subscribe(products => {
              let productNumberSet = new Set<number>();
              [...products.values()]
                .filter(item => !!item)
                .forEach(item => {
                  productNumberSet.add(item.productNumber);
                  if (item.hasAlternative && !!item.alternative?.product) {
                    productNumberSet.add(
                      item.alternative.product.productNumber,
                    );
                  }
                });
              if (!!searchViewModel.searchResponse?.alternativeProductNumbers) {
                searchViewModel.searchResponse.alternativeProductNumbers.forEach(
                  productNumber => productNumberSet.add(productNumber),
                );
              }
              this.productPricingService.loadPrices([...productNumberSet]);
            });
          return this.store
            .select(searchResultsSelector(previousPagesProductNumbers))
            .pipe(
              debounceTime(1),
              filter(items => {
                const itemsCount = [
                  ...new Set([
                    ...searchViewModel.searchResponse.productNumbers,
                    ...previousPagesProductNumbers,
                  ]),
                ].length;
                return items?.length === itemsCount;
              }),
              map((items: SearchResultViewModel[]) => {
                searchViewModel.listProducts = items;
                const navigationFilters = isGuest
                  ? searchViewModel.searchResponse?.navigation?.filter(
                      nav =>
                        nav?.filterName?.toUpperCase() !==
                          this.LISTS_FACET_NAME &&
                        nav?.filterName?.toUpperCase() !==
                          this.DEALS_FACET_NAME,
                    )
                  : searchViewModel?.searchResponse?.navigation;
                return {
                  ...searchViewModel,
                  searchResponse: {
                    ...searchViewModel.searchResponse,
                    navigation: navigationFilters,
                  },
                };
              }),
            );
        }
        return of(searchViewModel);
      }),
    );
  }

  /**
   * Gets OrderItem information to populate quantity boxes based on current order (cart)
   * @param productNumber
   */
  getOrderItem(productNumber: number): Observable<OrderItem> {
    return this.store.select(searchResultOrderItem(productNumber));
  }

  searchViewModelSelector$(): Observable<SearchViewModel> {
    return this.store.select(searchViewModelSelector);
  }

  searchResultsSelector$(
    searchViewModel: SearchViewModel,
  ): Observable<SearchViewModel> {
    return this.store
      .select(
        searchResultsSelector(
          searchViewModel?.listProducts?.map(p => p.productNumber),
        ),
      )
      .pipe(
        debounceTime(1),
        filter(items => items?.length >= 0),
        map((items: SearchResultViewModel[]) => {
          searchViewModel.listProducts = items;
          return searchViewModel;
        }),
      );
  }

  trackCatalogSubcategoryPageLoad(filters: string) {
    this.analyticsService.trackCatalogSubcategoryPageLoad(filters);
  }

  trackTogglingUsFoodsDeliveredProductsOn() {
    this.analyticsService.trackTogglingUsFoodsDeliveredProductsOn();
  }
  trackTogglingUsFoodsDeliveredProductsOff() {
    this.analyticsService.trackTogglingUsFoodsDeliveredProductsOff();
  }

  trackSearchResultPageLoad(
    bannerImageIds: string,
    filters: string,
    searchType?: string,
    productNumbers?: any[],
    searchTerm?: string,
  ) {
    this.analyticsService.trackSearchResultPageLoad(
      bannerImageIds,
      filters,
      searchType,
      productNumbers,
      searchTerm,
      false,
    );
  }

  trackVisualCartridgeBanner(bannerImageIds: string) {
    this.analyticsService.trackVisualCartridgeBanner(bannerImageIds);
  }

  trackMoreSearchResultsLoading(productNumbers: any[]) {
    this.analyticsService.trackMoreSearchResultsLoading(productNumbers);
  }

  trackWhileSuppliesLastClick() {
    this.analyticsService.trackClickWhileSuppliesLastBanner();
  }

  trackSearchPageSingleBanner(promoId: string) {
    this.analyticsService.trackSearchPageSingleBanner(promoId);
  }

  async routeToPDP(productNumber: number, isQuickView: boolean) {
    if (isQuickView) {
      this.navigationHelperService.routeToProductDetailsQueryParams(
        productNumber,
        { lnksrc: 'quickView' },
      );
    }
    this.navigationHelperService.routeToProductDetails(productNumber);
  }

  goToWhileSuppliesLast() {
    this.navigationHelperService.routeToSearch({
      searchText: SEARCH_PAGES_SEARCH_KEYS.whileSuppliesLast,
    });
    this.searchTracingService.traceWhileSuppliesLast();
  }

  goToDirect() {
    this.navigationHelperService.routeToSearch({
      searchFilterProperties: environment.direct,
    });
  }

  private loadProductDetails$(productNumbers: number[]) {
    return this.productService.loadProducts$(productNumbers).pipe(
      first(dispatched => dispatched),
      tap(_ => {
        this.productService.loadProductDetails(productNumbers);
      }),
    );
  }
  getProductDetailViewModels(
    productNumbers: number[],
    isDebounce = true,
  ): Observable<Map<number, ProductDetailViewModel>> {
    return this.store
      .select(productDetailsViewModelSelector(productNumbers))
      .pipe(
        filter(productDetails => !!productDetails),
        debounceTime(isDebounce ? 500 : 0),
      );
  }

  getProductDetails(
    productNumbers: number[],
    isDebounce = true,
  ): Observable<Map<number, ProductDetailViewModel>> {
    return this.loadProductDetails$(productNumbers).pipe(
      switchMap(() =>
        this.getProductDetailViewModels(productNumbers, isDebounce),
      ),
    );
  }

  getCoveoTopSellersProductDetailViewModels(
    productNumbers: number[],
    isDebounce = true,
  ): Observable<ProductDetailViewModel[]> {
    return this.store
      .select(coveoTopSellersViewModelSelector(productNumbers))
      .pipe(
        filter(productDetails => !!productDetails),
        debounceTime(isDebounce ? 500 : 0),
      );
  }

  getCoveoTopSellersProducts(
    productNumbers: number[],
    isDebounce = true,
  ): Observable<ProductDetailViewModel[]> {
    return this.loadProductDetails$(productNumbers).pipe(
      switchMap(() =>
        this.getCoveoTopSellersProductDetailViewModels(
          productNumbers,
          isDebounce,
        ),
      ),
    );
  }

  downloadSearchResults(SearchRequest: any): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    const apiUrl = `${environment.productApiUrl}/search/download`;

    return this.httpClient
      .post(apiUrl, SearchRequest, {
        headers: headers,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => errorMessage);
  }

  displayDownloadLimitMessage(message: string) {
    this.toastService.presentToast({
      id: '',
      type: MessageTypeEnum.warning,
      display: message,
      stack: '',
      watermark: '',
      read: false,
      sticky: false,
    });
  }

  trackTopSellersNullResults(productNumbers: ProductDetailViewModel[]) {
    this.analyticsService.trackSearchCarouselLoaded(
      productNumbers.map(product => ({
        divisionApn: `${product.summary?.divisionNumber}-${product.productNumber}`,
      })),
      'top sellers null results displayed',
    );
  }
}
